import { apiAxios } from "./axios";

export interface ConsultantActivity {
  id: string;
  consultant_id: string;
  consultant_name: string;
  supervisor_name: string;
  matches_received: number;
  confirmed: number;
  rejected: number;
  timed_out: number;
  pending_consultant: number;
  pending_supervisor: number;
  sent_back: number;
  sent_to_sales: number;
  sent_out: number;
  avg_response_time: string;
  supervisor_avg_response_time: string;
  period_start: string;
  period_end: string;
}

export interface ActivityResponse {
  success: boolean;
  time_window: string;
  data: ConsultantActivity[];
  count: number;
  message?: string;
}

export interface CalculateActivityResponse {
  success: boolean;
  message: string;
  details?: any;
}

export interface DailyMatchesEntry {
  date: string; // YYYY-MM-DD
  matches: number;
  confirmed: number;
  rejected: number;
  filtered?: number; // absent on snapshots taken before filtered_out was recorded
}

export interface ConsultantActivityRange {
  consultant_id: string;
  consultant_name: string;
  supervisor_name: string;
  current_status: "active" | "inactive";
  matches_received: number;
  confirmed: number;
  rejected: number;
  timed_out: number;
  pending_consultant: number;
  pending_supervisor: number;
  sent_back: number;
  sent_to_sales: number;
  sent_out: number;
  avg_response_time: string;
  supervisor_avg_response_time: string;
  daily_matches: DailyMatchesEntry[];
}

export interface ActivityRangeResponse {
  success: boolean;
  start: string;
  end: string;
  data: ConsultantActivityRange[];
  count: number;
  message?: string;
}

class ActivityService {
  /**
   * @param timeWindow - Format: "daily_YYYY-MM-DD" or "weekly_YYYY-MM-DD"
   */
  async getActivity(timeWindow: string): Promise<ActivityResponse> {
    const response = await apiAxios.get<ActivityResponse>(
      `/activity/${timeWindow}`
    );
    return response.data;
  }

  /**
   * @param start ISO date string YYYY-MM-DD (inclusive)
   * @param end   ISO date string YYYY-MM-DD (inclusive)
   * @param consultantIds optional list of consultant IDs to focus on
   */
  async getActivityRange(
    start: string,
    end: string,
    consultantIds?: string[],
    signal?: AbortSignal,
  ): Promise<ActivityRangeResponse> {
    const params: Record<string, string> = { start, end };
    if (consultantIds && consultantIds.length > 0) {
      params.consultant_ids = consultantIds.join(",");
    }
    const response = await apiAxios.get<ActivityRangeResponse>("/activity", {
      params,
      signal,
    });
    return response.data;
  }

  /**
   * @param days - Number of days to calculate (1 for daily, 7 for weekly)
   */
  async calculateActivity(days: number): Promise<CalculateActivityResponse> {
    const response = await apiAxios.post<CalculateActivityResponse>(
      "/calculate-activity",
      { days }
    );
    return response.data;
  }

  getTodayDateString(): string {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, "0");
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const year = today.getFullYear();
    return `${day}-${month}-${year}`;
  }

  getDailyTimeWindow(): string {
    return `${this.getTodayDateString()}_daily`;
  }

  getWeeklyTimeWindow(): string {
    return `${this.getTodayDateString()}_weekly`;
  }
}

export default new ActivityService();
